import { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Seo from '@/components/Seo';
import { toast } from 'sonner';
import {
  Star, Clock, ShieldCheck, ArrowLeft, ArrowRight, MapPin, Phone,
  MessageCircle, Award, BadgeCheck, Languages, Search, ChevronDown,
  Sparkles, Briefcase, CheckCircle2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ServiceCategoryPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState({});  // provider_id -> true (phone shown)
  const [cityFilter, setCityFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/services/category/${slug}`);
        if (!res.ok) {
          toast.error('Category not found');
          navigate('/services');
          return;
        }
        setData(await res.json());
      } finally { setLoading(false); }
    };
    load();
    window.scrollTo(0, 0);
  }, [slug, navigate]);

  const providers = data?.providers || [];
  const cities = useMemo(() => Array.from(new Set(providers.map((p) => p.city))).sort(), [providers]);

  const filtered = useMemo(() => {
    let list = providers;
    if (cityFilter !== 'all') list = list.filter((p) => p.city === cityFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((p) =>
        p.name.toLowerCase().includes(q) ||
        p.role.toLowerCase().includes(q) ||
        p.city.toLowerCase().includes(q) ||
        p.achievements.some((a) => a.toLowerCase().includes(q))
      );
    }
    return list;
  }, [providers, cityFilter, search]);

  if (loading || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F172A]"></div>
      </div>
    );
  }

  const { category, services } = data;
  const reveal = (id) => setRevealed((r) => ({ ...r, [id]: true }));
  const onCall = (p) => { reveal(p.provider_id); window.location.href = `tel:${p.phone.replace(/\s/g, '')}`; };
  const onWhatsApp = (p) => {
    reveal(p.provider_id);
    const phoneNum = p.phone.replace(/[\s+]/g, '');
    const text = encodeURIComponent(`Hi ${p.name.split(' ')[0]}, I found you on Bharat Estate and would like to discuss a ${category.name} job.`);
    window.open(`https://wa.me/${phoneNum}?text=${text}`, '_blank');
  };

  return (
    <div className="min-h-screen panel-soft" data-testid="category-page">
      <Seo
        title={`${category.name} in 24+ Cities · Verified Pros · Bharat Estate`}
        description={`${category.tagline} Verified ${category.name} technicians with phone numbers, ratings, and experience details.`}
        path={`/services/${category.slug}`}
      />

      {/* HERO */}
      <section className="relative pt-36 pb-16 overflow-hidden" style={{ backgroundColor: category.color }}>
        <div className="absolute inset-0 opacity-30">
          <img src={category.hero_image} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A]/85 via-[#0F172A]/65 to-transparent" />
        {/* subtle dot pattern overlay */}
        <div className="absolute inset-0 dot-pattern opacity-30" />
        <div className="container-luxury relative text-white">
          <Link to="/services" className="inline-flex items-center gap-2 text-white/80 hover:text-[#D4AF37] text-sm mb-4">
            <ArrowLeft className="h-4 w-4" /> All Services
          </Link>
          <h1 className="font-['Plus_Jakarta_Sans'] text-4xl md:text-6xl font-bold leading-tight mb-3">{category.name}</h1>
          <p className="text-white/80 text-lg max-w-2xl">{category.tagline}</p>
          <div className="flex flex-wrap gap-5 mt-6 text-sm">
            <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#D4AF37]" /> Verified Pros</span>
            <span className="inline-flex items-center gap-2"><Star className="h-4 w-4 fill-[#D4AF37] text-[#D4AF37]" /> 4.8 avg rating</span>
            <span className="inline-flex items-center gap-2"><Phone className="h-4 w-4 text-[#D4AF37]" /> Direct phone numbers</span>
            <span className="inline-flex items-center gap-2"><MessageCircle className="h-4 w-4 text-[#D4AF37]" /> WhatsApp enabled</span>
          </div>
        </div>
      </section>

      {/* FILTER BAR */}
      <section className="sticky top-20 z-30 bg-white/80 backdrop-blur-xl border-y border-border/50">
        <div className="container-luxury py-3 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <div className="flex flex-1 items-center gap-2 max-w-md">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${category.name.toLowerCase()} pros by name, city, achievement…`}
              className="flex-1 bg-transparent outline-none text-sm text-[#0F172A] placeholder:text-muted-foreground/70 py-2"
              data-testid="directory-search-input"
            />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto">
            <button onClick={() => setCityFilter('all')} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${cityFilter === 'all' ? 'bg-[#0F172A] text-white' : 'bg-[#F9F9F7] text-[#0F172A] hover:bg-[#D4AF37]/15'}`} data-testid="city-filter-all">All cities ({providers.length})</button>
            {cities.map((c) => (
              <button key={c} onClick={() => setCityFilter(c)} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${cityFilter === c ? 'bg-[#0F172A] text-white' : 'bg-[#F9F9F7] text-[#0F172A] hover:bg-[#D4AF37]/15'}`} data-testid={`city-filter-${c}`}>{c}</button>
            ))}
          </div>
        </div>
      </section>

      {/* DIRECTORY */}
      <section className="py-12">
        <div className="container-luxury">
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="font-['Plus_Jakarta_Sans'] text-3xl md:text-4xl font-bold text-[#0F172A]">{filtered.length} {category.name} pros</h2>
              <p className="text-muted-foreground text-sm mt-1">{cityFilter === 'all' ? 'Across India' : `In ${cityFilter}`} · Verified by Bharat Estate</p>
            </div>
            <div className="hidden md:flex items-center gap-2 text-xs text-muted-foreground">
              <Sparkles className="h-3 w-3 text-[#D4AF37]" /> Sorted by rating &amp; jobs
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="bg-white rounded-sm border border-dashed p-12 text-center">
              <p className="text-[#0F172A] font-semibold text-lg mb-2">No pros found here yet.</p>
              <p className="text-muted-foreground text-sm mb-4">Try a different city, clear filters, or be the first to register.</p>
              <Link to="/become-a-pro"><Button className="bg-[#0F172A] text-white">Register yourself</Button></Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {filtered.map((p) => (
                <article key={p.provider_id} className="bg-white rounded-sm border border-border/50 p-5 hover:shadow-lg hover:border-[#D4AF37]/40 transition-all" data-testid={`provider-card-${p.provider_id}`}>
                  <div className="flex gap-4">
                    <div className="relative flex-shrink-0">
                      <img src={p.photo} alt={p.name} className="h-20 w-20 rounded-sm object-cover ring-2 ring-[#D4AF37]/40" />
                      {p.verified && (
                        <span className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-[#10B981] flex items-center justify-center ring-2 ring-white" title="Verified">
                          <BadgeCheck className="h-3.5 w-3.5 text-white" />
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="font-['Plus_Jakarta_Sans'] text-xl font-bold text-[#0F172A] leading-tight">{p.name}</h3>
                          <p className="text-xs text-muted-foreground mt-0.5">{p.role}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="inline-flex items-center gap-1 font-bold text-[#0F172A] text-sm">
                            <Star className="h-3.5 w-3.5 fill-[#D4AF37] text-[#D4AF37]" /> {p.rating}
                          </p>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">{p.jobs_done.toLocaleString('en-IN')} jobs</p>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3 text-[#D4AF37]" /> {p.city}, {p.state}</span>
                        <span className="inline-flex items-center gap-1"><Briefcase className="h-3 w-3 text-[#D4AF37]" /> {p.experience_years} yrs exp</span>
                        <span className="inline-flex items-center gap-1"><Languages className="h-3 w-3 text-[#D4AF37]" /> {p.languages.slice(0, 3).join(' · ')}</span>
                      </div>

                      {p.achievements?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {p.achievements.slice(0, 3).map((a) => (
                            <span key={a} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#D4AF37]/10 text-[#8B6F1F] border border-[#D4AF37]/30">
                              <Award className="h-2.5 w-2.5" /> {a}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Contact panel */}
                  <div className="mt-4 pt-4 border-t border-dashed border-border/60 flex flex-wrap gap-2 items-center">
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Phone</p>
                      <p className="font-mono text-sm font-semibold text-[#0F172A]" data-testid={`phone-${p.provider_id}`}>
                        {revealed[p.provider_id] ? p.phone : (
                          <button onClick={() => reveal(p.provider_id)} className="text-[#D4AF37] hover:underline text-sm" data-testid={`reveal-${p.provider_id}`}>
                            {p.phone.slice(0, 8)}••••• &nbsp;<span className="text-[10px] opacity-75">Tap to reveal</span>
                          </button>
                        )}
                      </p>
                    </div>
                    <Button size="sm" onClick={() => onCall(p)} className="bg-[#0F172A] hover:bg-[#1E293B] text-white" data-testid={`call-${p.provider_id}`}>
                      <Phone className="h-3.5 w-3.5 mr-1" /> Call
                    </Button>
                    <Button size="sm" onClick={() => onWhatsApp(p)} className="bg-[#25D366] hover:bg-[#1ea954] text-white" data-testid={`wa-${p.provider_id}`}>
                      <MessageCircle className="h-3.5 w-3.5 mr-1" /> WhatsApp
                    </Button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* SERVICES OFFERED (info only) */}
      <section className="py-16 panel-warm border-y border-border/50">
        <div className="container-luxury">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-[0.2em] mb-2">What's covered</p>
              <h2 className="font-['Plus_Jakarta_Sans'] text-3xl md:text-4xl font-bold text-[#0F172A]">Services in this category</h2>
              <p className="text-muted-foreground text-sm mt-1">Tell us what you need — our pro will share a tailored quote on the call.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {services.map((s) => (
              <div key={s.service_id} className="bg-white border border-border/50 rounded-sm p-4 flex gap-4 items-center" data-testid={`service-info-${s.service_id}`}>
                <img src={s.image} alt={s.name} className="h-16 w-16 object-cover rounded-sm flex-shrink-0" loading="lazy" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-[#0F172A] text-sm">{s.name}</h3>
                  <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{s.description}</p>
                  <div className="flex items-center gap-3 mt-1.5 text-[11px] text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {s.duration_mins >= 60 ? `${Math.round(s.duration_mins / 60)}h` : `${s.duration_mins}m`}</span>
                    <span className="inline-flex items-center gap-1"><Star className="h-3 w-3 fill-[#D4AF37] text-[#D4AF37]" /> {s.rating}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REGISTER AS A PRO */}
      <section className="py-16 relative overflow-hidden bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] text-white">
        <div className="absolute inset-0 dot-pattern opacity-10" />
        <div className="container-luxury relative">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7">
              <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-[0.2em] mb-3">Are you a {category.name} pro?</p>
              <h2 className="font-['Plus_Jakarta_Sans'] text-4xl md:text-5xl font-bold leading-tight mb-4">List yourself here. Free.</h2>
              <p className="text-white/75 leading-relaxed mb-6">
                Add your name to the {category.name} directory. Indians from {cities.length || '24+'} cities will see your profile and reach you on
                phone &amp; WhatsApp directly. No commission, no platform fee — just visibility.
              </p>
              <ul className="space-y-2 mb-7 text-sm">
                {[
                  'Free profile · phone number always visible to genuine buyers',
                  'Verified-pro badge after Aadhaar + work-sample check',
                  '24/7 helpline if a customer creates trouble',
                  'Optional: opt in to Bharat Estate paid leads for ₹0 monthly',
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2 text-white/80"><CheckCircle2 className="h-4 w-4 text-[#D4AF37] flex-shrink-0 mt-0.5" /> {b}</li>
                ))}
              </ul>
              <Link to="/become-a-pro">
                <Button className="bg-[#D4AF37] text-[#0F172A] hover:bg-[#B8932F] font-bold px-7 py-6" data-testid="register-pro-cta">
                  Register as a Pro <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
            <div className="md:col-span-5">
              <div className="grid grid-cols-2 gap-3 text-center">
                {[
                  { k: '8,400+', l: 'Active pros listed' },
                  { k: '24+', l: 'Cities covered' },
                  { k: '₹0', l: 'Listing fee' },
                  { k: '4.8★', l: 'Avg pro rating' },
                ].map((s) => (
                  <div key={s.l} className="bg-white/5 backdrop-blur border border-white/10 rounded-sm p-5">
                    <p className="font-['Plus_Jakarta_Sans'] text-4xl font-bold text-[#D4AF37]">{s.k}</p>
                    <p className="text-xs uppercase tracking-wider text-white/70 mt-1">{s.l}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SAFETY FOOTER */}
      <section className="py-8 panel-soft">
        <div className="container-luxury text-xs text-muted-foreground leading-relaxed">
          <strong className="text-[#0F172A]">Safety tip:</strong> Always verify ID, agree on a written estimate, and pay only after the job is complete to your satisfaction. Bharat Estate verifies pros with Aadhaar + police check, but rates and terms are between you and the pro.
        </div>
      </section>
    </div>
  );
};

export default ServiceCategoryPage;
